import { createClient } from '@supabase/supabase-js';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;
export const supabase = createClient(SUPABASE_URL, SUPABASE_KEY);

// ── USERS ─────────────────────────────────────────────────────────────────────

export async function getAllUsers() {
  const { data } = await supabase.from('users').select('*').order('username');
  return data || [];
}

export async function getUser(id) {
  const { data } = await supabase.from('users').select('*').eq('id', id).single();
  return data;
}

export async function createUser(userData) {
  const { data, error } = await supabase.from('users').insert(userData).select().single();
  if (error) throw error;
  return data;
}

export async function updateUser(id, updates) {
  const { data, error } = await supabase.from('users').update(updates).eq('id', id);
  if (error) console.error('updateUser error:', error);
  return { data, error };
}

export async function searchUsers(query) {
  const { data } = await supabase.from('users')
    .select('id, username, bio, post_count, photo_url')
    .textSearch('search_vector', query);
  return data || [];
}

// ── THREADS ───────────────────────────────────────────────────────────────────

export async function getAllThreads() {
  const { data } = await supabase.from('threads')
    .select('*')
    .order('created_at', { ascending: false });
  return data || [];
}

export async function getThreadsByBoard(boardId) {
  const { data } = await supabase.from('threads')
    .select('*')
    .eq('board_id', boardId)
    .order('is_pinned', { ascending: false })
    .order('created_at', { ascending: false });
  return data || [];
}

export async function createThread(threadData) {
  const { data, error } = await supabase.from('threads').insert(threadData).select().single();
  if (error) throw error;
  return data;
}

export async function updateThread(id, updates) {
  const { error } = await supabase.from('threads').update(updates).eq('id', id);
  if (error) throw error;
}

export async function deleteThread(id) {
  // Delete replies first, then the thread itself
  await supabase.from('posts').delete().eq('thread_id', id);
  const { error } = await supabase.from('threads').delete().eq('id', id);
  if (error) throw error;
}

export async function incrementThreadViews(id) {
  await supabase.rpc('increment_thread_views', { tid: id });
}

export async function searchThreads(query) {
  const { data } = await supabase.from('threads')
    .select('id, title, content, board_id, created_at, author_name')
    .textSearch('search_vector', query)
    .eq('is_anon', false)
    .order('created_at', { ascending: false });
  return data || [];
}

// ── POSTS ─────────────────────────────────────────────────────────────────────

export async function getAllPosts() {
  const { data } = await supabase.from('posts')
    .select('*')
    .order('created_at', { ascending: true });
  return data || [];
}

export async function getPostsByThread(threadId) {
  const { data } = await supabase.from('posts')
    .select('*')
    .eq('thread_id', threadId)
    .order('created_at', { ascending: true });
  return data || [];
}

export async function createPost(postData) {
  const { data, error } = await supabase.from('posts').insert(postData).select().single();
  if (error) throw error;
  return data;
}

export async function updatePost(id, updates) {
  const { error } = await supabase.from('posts').update(updates).eq('id', id);
  if (error) throw error;
}

// ── PROFILE COMMENTS ──────────────────────────────────────────────────────────

export async function getProfileComments(userId) {
  const { data } = await supabase.from('profile_comments')
    .select('*')
    .eq('target_user_id', userId)
    .order('created_at', { ascending: true });
  return data || [];
}

export async function addProfileComment(targetUserId, authorId, authorName, content) {
  const { error } = await supabase.from('profile_comments')
    .insert({ target_user_id: targetUserId, author_id: authorId, author_name: authorName, content });
  if (error) throw error;
}

// ── PROFILE VISITS ────────────────────────────────────────────────────────────

export async function trackVisit(profileUserId, visitorId, visitorName) {
  await supabase.from('profile_visits').upsert({
    profile_user_id: profileUserId,
    visitor_id: visitorId,
    visitor_name: visitorName,
    visited_at: new Date().toISOString(),
  }, { onConflict: 'profile_user_id,visitor_id' });
  await supabase.rpc('increment_user_views', { uid: profileUserId });
}

export async function getRecentVisitors(profileUserId) {
  const { data } = await supabase.from('profile_visits')
    .select('visitor_id, visitor_name, visited_at')
    .eq('profile_user_id', profileUserId)
    .order('visited_at', { ascending: false })
    .limit(12);
  return data || [];
}

// ── BOARD MODS ────────────────────────────────────────────────────────────────

export async function getBoardMods() {
  const { data } = await supabase.from('board_mods').select('board_id, user_id');
  const result = {};
  (data || []).forEach(row => {
    if (!result[row.board_id]) result[row.board_id] = [];
    result[row.board_id].push(row.user_id);
  });
  return result;
}

export async function addBoardMod(boardId, userId) {
  const { error } = await supabase.from('board_mods').insert({ board_id: boardId, user_id: userId });
  if (error) throw error;
}

export async function removeBoardMod(boardId, userId) {
  const { error } = await supabase.from('board_mods')
    .delete().eq('board_id', boardId).eq('user_id', userId);
  if (error) throw error;
}

// ── BOARD STYLES ──────────────────────────────────────────────────────────────

function styleToDb(s) {
  return {
    bg: s.bg,
    header_bg: s.headerBg,
    header_text: s.headerText,
    text_color: s.textColor,
    link_color: s.linkColor,
    border_color: s.borderColor,
    table_row_bg: s.tableRowBg,
    table_row_alt: s.tableRowAlt,
    font: s.font,
    banner: s.banner,
    rules: s.rules,
  };
}

function styleFromDb(row) {
  return {
    bg: row.bg,
    headerBg: row.header_bg,
    headerText: row.header_text,
    textColor: row.text_color,
    linkColor: row.link_color,
    borderColor: row.border_color,
    tableRowBg: row.table_row_bg,
    tableRowAlt: row.table_row_alt,
    font: row.font,
    banner: row.banner || '',
    rules: row.rules || '',
  };
}

export async function getBoardStyles() {
  const { data } = await supabase.from('board_styles').select('*');
  const result = {};
  (data || []).forEach(row => { result[row.board_id] = styleFromDb(row); });
  return result;
}

export async function saveBoardStyle(boardId, style) {
  const { error } = await supabase.from('board_styles')
    .upsert({ board_id: boardId, ...styleToDb(style), updated_at: new Date().toISOString() });
  if (error) throw error;
}

// ── MEDIA ─────────────────────────────────────────────────────────────────────

export async function uploadMedia(dataUrl, path) {
  const [header, data] = dataUrl.split(',');
  const mime = header.match(/:(.*?);/)[1];
  const binary = atob(data);
  const array = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) array[i] = binary.charCodeAt(i);
  const file = new File([array], path, { type: mime });

  const { error } = await supabase.storage
    .from('southharbor-media')
    .upload(path, file, { upsert: true });
  if (error) throw error;

  const { data: { publicUrl } } = supabase.storage
    .from('southharbor-media')
    .getPublicUrl(path);
  return publicUrl;
}

const EXT_MIME = {
  jpg:'image/jpeg',jpeg:'image/jpeg',png:'image/png',gif:'image/gif',
  webp:'image/webp',heic:'image/heic',heif:'image/heif',
  mp3:'audio/mpeg',m4a:'audio/mp4',aac:'audio/aac',
  ogg:'audio/ogg',wav:'audio/wav',flac:'audio/flac',webm:'audio/webm',
};
export async function uploadFile(file, path) {
  const ext = file.name.split('.').pop().toLowerCase();
  const contentType = file.type || EXT_MIME[ext] || 'application/octet-stream';
  // Read into ArrayBuffer so upload works consistently across all browsers/mobile
  const arrayBuffer = await file.arrayBuffer();
  const blob = new Blob([arrayBuffer], { type: contentType });
  const { error } = await supabase.storage
    .from('southharbor-media')
    .upload(path, blob, { upsert: true, contentType });
  if (error) throw error;
  const { data: { publicUrl } } = supabase.storage
    .from('southharbor-media')
    .getPublicUrl(path);
  return publicUrl;
}

export async function deleteMedia(path) {
  if (!path) return;
  const filename = path.split('/').pop();
  await supabase.storage.from('southharbor-media').remove([filename]);
}

// ── REACTIONS ─────────────────────────────────────────────────────────────────
// targetType: 'post' | 'thread'
// The reactions table has two nullable FK columns — post_id and thread_id —
// so that both replies (posts table) and OP threads (threads table) can be
// reacted to without ID-space collisions between the two tables.

export async function getReactions(ids, targetType = 'post') {
  if (!ids || !ids.length) return [];
  const col = targetType === 'thread' ? 'thread_id' : 'post_id';
  const { data, error } = await supabase
    .from('reactions')
    .select('post_id, thread_id, user_id, type')
    .in(col, ids);
  if (error) { console.error('getReactions error:', error.message); return []; }
  return data || [];
}

export async function upsertReaction(targetId, userId, type, targetType = 'post') {
  const col = targetType === 'thread' ? 'thread_id' : 'post_id';

  // Partial unique indexes (WHERE col IS NOT NULL) cannot be used as explicit
  // ON CONFLICT targets by PostgREST — upsert would silently insert duplicates.
  // Instead: check for an existing row, then UPDATE using the natural key or INSERT.
  // NOTE: do NOT select 'id' — the reactions table may not have an id PK column.
  const { data: existing, error: selErr } = await supabase
    .from('reactions')
    .select('type')
    .eq(col, targetId)
    .eq('user_id', userId)
    .maybeSingle();
  if (selErr) { console.error('upsertReaction select error:', selErr.message, selErr.details); throw selErr; }

  if (existing) {
    // Row exists — UPDATE by natural key (col + user_id), not by id
    const { error } = await supabase
      .from('reactions')
      .update({ type })
      .eq(col, targetId)
      .eq('user_id', userId);
    if (error) { console.error('upsertReaction update error:', error.message, error.details); throw error; }
  } else {
    const { error } = await supabase
      .from('reactions')
      .insert({ [col]: targetId, user_id: userId, type });
    if (error) { console.error('upsertReaction insert error:', error.message, error.details); throw error; }
  }
}

export async function removeReaction(targetId, userId, targetType = 'post') {
  const col = targetType === 'thread' ? 'thread_id' : 'post_id';
  const { error } = await supabase
    .from('reactions')
    .delete()
    .eq(col, targetId)
    .eq('user_id', userId);
  if (error) throw error;
}

// Returns total likes and dislikes received across all a user's posts and threads.
// Batch version: fetches all content for all userIds in 4 parallel queries total,
// never N per-user.  Returns { [userId]: { likes, dislikes } }.
export async function getUserReactionTotalsBatch(userIds) {
  if (!userIds || !userIds.length) return {};

  // Step 1: fetch all content IDs for these users (2 queries in parallel)
  const [{ data: allPosts }, { data: allThreads }] = await Promise.all([
    supabase.from('posts').select('id, author_id').in('author_id', userIds).eq('deleted', false),
    supabase.from('threads').select('id, author_id').in('author_id', userIds).eq('is_anon', false),
  ]);

  const postIds   = (allPosts   || []).map(p => p.id);
  const threadIds = (allThreads || []).map(t => t.id);

  // Reverse maps: contentId → authorId
  const postAuthor   = {};
  for (const p of allPosts   || []) postAuthor[p.id]   = p.author_id;
  const threadAuthor = {};
  for (const t of allThreads || []) threadAuthor[t.id] = t.author_id;

  // Step 2: fetch all reactions for those content IDs (2 queries in parallel)
  const [postRxns, threadRxns] = await Promise.all([
    postIds.length
      ? supabase.from('reactions').select('post_id, type').in('post_id', postIds)
      : Promise.resolve({ data: [] }),
    threadIds.length
      ? supabase.from('reactions').select('thread_id, type').in('thread_id', threadIds)
      : Promise.resolve({ data: [] }),
  ]);

  // Step 3: tally per-user
  const totals = {};
  const ensure = uid => { if (!totals[uid]) totals[uid] = { likes: 0, dislikes: 0, threadCount: 0 }; };

  // Count threads per author (already fetched above)
  for (const t of allThreads || []) {
    ensure(t.author_id);
    totals[t.author_id].threadCount++;
  }

  for (const r of postRxns.data || []) {
    const uid = postAuthor[r.post_id];
    if (!uid) continue;
    ensure(uid);
    if (r.type === 'like') totals[uid].likes++; else totals[uid].dislikes++;
  }
  for (const r of threadRxns.data || []) {
    const uid = threadAuthor[r.thread_id];
    if (!uid) continue;
    ensure(uid);
    if (r.type === 'like') totals[uid].likes++; else totals[uid].dislikes++;
  }

  return totals;
}

// Returns total likes and dislikes received across all a user's posts and threads.
export async function getUserReactionTotals(userId) {
  const [{ data: userPosts }, { data: userThreads }] = await Promise.all([
    supabase.from('posts').select('id').eq('author_id', userId).eq('deleted', false),
    supabase.from('threads').select('id').eq('author_id', userId).eq('is_anon', false),
  ]);

  const postIds   = (userPosts   || []).map(p => p.id);
  const threadIds = (userThreads || []).map(t => t.id);

  let likes = 0, dislikes = 0;

  const [postRxns, threadRxns] = await Promise.all([
    postIds.length
      ? supabase.from('reactions').select('type').in('post_id', postIds)
      : Promise.resolve({ data: [] }),
    threadIds.length
      ? supabase.from('reactions').select('type').in('thread_id', threadIds)
      : Promise.resolve({ data: [] }),
  ]);

  for (const r of [...(postRxns.data || []), ...(threadRxns.data || [])]) {
    if (r.type === 'like') likes++; else dislikes++;
  }
  return { likes, dislikes };
}

// Returns a map of threadId -> total like count across the OP reaction AND all reply
// reactions for each thread.  Accepts the thread objects (already in state) so it can
// build the replyId→threadId map without an extra query.
export async function getThreadLikeTotals(threads) {
  if (!threads || !threads.length) return {};

  const threadIds = threads.map(t => t.id);

  // Build { replyPostId: threadId } from the in-memory thread objects
  const replyIdToThread = {};
  for (const t of threads) {
    for (const r of t.replies || []) {
      if (r.id) replyIdToThread[r.id] = t.id;
    }
  }
  const replyIds = Object.keys(replyIdToThread).map(Number);

  const [threadRxns, postRxns] = await Promise.all([
    supabase.from('reactions').select('thread_id').in('thread_id', threadIds).eq('type', 'like'),
    replyIds.length
      ? supabase.from('reactions').select('post_id').in('post_id', replyIds).eq('type', 'like')
      : Promise.resolve({ data: [] }),
  ]);

  const totals = {};
  for (const r of threadRxns.data || []) {
    totals[r.thread_id] = (totals[r.thread_id] || 0) + 1;
  }
  for (const r of postRxns.data || []) {
    const tid = replyIdToThread[r.post_id];
    if (tid) totals[tid] = (totals[tid] || 0) + 1;
  }
  return totals;
}

// ── RECENTLY REPLIED ──────────────────────────────────────────────────────────

// Returns up to `limit` threads that have had recent replies, ordered by most
// recent reply descending, deduplicated so each thread appears only once.
export async function getRecentlyReplied(limit = 8) {
  // Fetch recent non-deleted posts with their parent thread embedded.
  // Fetch more than `limit` so deduplication still leaves us enough.
  const { data, error } = await supabase
    .from('posts')
    .select('thread_id, created_at, author_name, is_anon, threads(id, title, board_id, is_anon)')
    .eq('deleted', false)
    .order('created_at', { ascending: false })
    .limit(limit * 8);

  if (error) { console.error('getRecentlyReplied error:', error.message); return []; }

  const seen = new Set();
  const result = [];
  for (const row of (data || [])) {
    if (!row.threads || seen.has(row.thread_id)) continue;
    seen.add(row.thread_id);
    result.push({
      threadId: row.thread_id,
      threadTitle: row.threads.title,
      boardId: row.threads.board_id,
      isAnon: row.threads.is_anon,
      lastReplyAt: row.created_at,
      lastReplyBy: row.is_anon ? "Anonymous" : (row.author_name || "Anonymous"),
    });
    if (result.length >= limit) break;
  }
  return result;
}

// ── BOARD INITIALIZATION ───────────────────────────────────────────────────────

// Some Supabase schemas have a 'boards' reference table that other tables
// (threads, board_mods, board_styles) foreign-key into. This function upserts
// all known board IDs so new boards aren't blocked by FK constraints.
//
// NOTE: Supabase JS does NOT throw on errors — it returns { data, error }.
// We must check `error` explicitly; try/catch alone won't catch DB-level failures.
export async function ensureBoards(boards) {
  // Step 1: Read one row to confirm the table exists and detect the PK column name.
  const { data: peek, error: peekErr } = await supabase.from('boards').select('*').limit(1);
  if (peekErr) {
    console.error('ensureBoards: cannot read boards table:', peekErr.message, peekErr.details);
    return;
  }

  // Step 2: Detect whether the PK is named 'id' or 'board_id'.
  const sample = peek && peek[0];
  const pkCol = sample && 'board_id' in sample ? 'board_id' : 'id';
  console.log(`ensureBoards: boards table found, PK column = "${pkCol}", existing rows:`, peek);

  // Step 3: Upsert only the PK column — avoid assuming any other columns exist.
  const rows = boards.map(b => ({ [pkCol]: b.id }));
  const { error: upsertErr } = await supabase
    .from('boards')
    .upsert(rows, { onConflict: pkCol });

  if (upsertErr) {
    console.error('ensureBoards: upsert failed:', upsertErr.message, upsertErr.details, upsertErr.hint);
  } else {
    console.log('ensureBoards: successfully upserted', rows.length, 'boards');
  }
}

// ── NOTIFICATIONS ─────────────────────────────────────────────────────────────

export async function getNotifications(userId) {
  const { data, error } = await supabase
    .from('notifications')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(50);
  if (error) { console.error('getNotifications error:', error.message); return []; }
  return data || [];
}

export async function createNotification(userId, type, fromUserId, data = {}) {
  const { error } = await supabase.from('notifications').insert({
    user_id: userId, type, from_user_id: fromUserId, data, read: false,
  });
  if (error) console.error('createNotification error:', error.message, error.details);
}

export async function markNotificationRead(notifId) {
  const { error } = await supabase.from('notifications').update({ read: true }).eq('id', notifId);
  if (error) console.error('markNotificationRead error:', error.message);
}

export async function markAllNotificationsRead(userId) {
  const { error } = await supabase.from('notifications')
    .update({ read: true }).eq('user_id', userId).eq('read', false);
  if (error) console.error('markAllNotificationsRead error:', error.message);
}

// ── FRIENDSHIPS ───────────────────────────────────────────────────────────────

export async function getFriendships(userId) {
  const { data, error } = await supabase
    .from('friendships')
    .select('*')
    .or(`requester_id.eq.${userId},addressee_id.eq.${userId}`);
  if (error) { console.error('getFriendships error:', error.message); return []; }
  return data || [];
}

export async function sendFriendRequest(requesterId, addresseeId) {
  const { data, error } = await supabase
    .from('friendships')
    .insert({ requester_id: requesterId, addressee_id: addresseeId, status: 'pending' })
    .select().single();
  if (error) throw error;
  return data;
}

export async function acceptFriendRequest(friendshipId) {
  const { error } = await supabase
    .from('friendships').update({ status: 'accepted' }).eq('id', friendshipId);
  if (error) throw error;
}

export async function declineFriendRequest(friendshipId) {
  const { error } = await supabase
    .from('friendships').update({ status: 'declined' }).eq('id', friendshipId);
  if (error) throw error;
}

export async function removeFriend(friendshipId) {
  const { error } = await supabase.from('friendships').delete().eq('id', friendshipId);
  if (error) throw error;
}

// ── PROFILE POSTS ─────────────────────────────────────────────────────────────

export async function getProfilePosts(userId) {
  const { data, error } = await supabase
    .from('profile_posts')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });
  if (error) { console.error('getProfilePosts error:', error.message); return []; }
  return data || [];
}

export async function createProfilePost(userId, content, imageUrl = null) {
  const { data, error } = await supabase
    .from('profile_posts')
    .insert({ user_id: userId, content: content || '', image_url: imageUrl || null })
    .select().single();
  if (error) throw error;
  return data;
}

export async function deleteProfilePost(postId) {
  const { error } = await supabase.from('profile_posts').delete().eq('id', postId);
  if (error) throw error;
}

// ── PROFILE ALBUMS ────────────────────────────────────────────────────────────

export async function getProfileAlbums(userId) {
  const { data, error } = await supabase
    .from('profile_albums')
    .select('*, album_items(*)')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });
  if (error) { console.error('getProfileAlbums error:', error.message); return []; }
  // Sort items within each album by sort_order
  for (const a of data || []) {
    (a.album_items || []).sort((x, y) => (x.sort_order || 0) - (y.sort_order || 0));
  }
  return data || [];
}

export async function createAlbum(userId, title, type) {
  const { data, error } = await supabase
    .from('profile_albums')
    .insert({ user_id: userId, title, type })
    .select().single();
  if (error) throw error;
  return { ...data, album_items: [] };
}

export async function deleteAlbum(albumId) {
  const { error } = await supabase.from('profile_albums').delete().eq('id', albumId);
  if (error) throw error;
}

export async function addAlbumItem(albumId, url, title, sortOrder = 0) {
  const { data, error } = await supabase
    .from('album_items')
    .insert({ album_id: albumId, url, title: title || null, sort_order: sortOrder })
    .select().single();
  if (error) throw error;
  return data;
}

export async function deleteAlbumItem(itemId) {
  const { error } = await supabase.from('album_items').delete().eq('id', itemId);
  if (error) throw error;
}
